import api, {
  buildCompanyScopeHeaders,
} from "./api";


export const PURCHASE_ORDER_STATUSES = Object.freeze([
  "draft",
  "submitted",
  "approved",
  "ready_to_dispatch",
  "dispatching",
  "dispatched",
  "acknowledged",
  "partially_received",
  "received",
  "closed",
  "dispatch_failed",
  "rejected",
  "cancelled",
]);


export const PURCHASE_ORDER_SORT_FIELDS = Object.freeze([
  "created_at",
  "order_reference",
  "status",
  "supplier_uuid",
]);


export const PURCHASE_ORDER_SORT_DIRECTIONS = Object.freeze([
  "asc",
  "desc",
]);


export const PURCHASE_ORDER_EXPORT_FORMATS =
  Object.freeze([
    "json",
    "csv",
    "txt",
    "xlsx",
  ]);


function normalizeOptionalString(value) {
  if (typeof value !== "string") {
    return "";
  }

  return value.trim();
}


function requireNonEmptyString(value, fieldName) {
  const normalizedValue = normalizeOptionalString(value);

  if (!normalizedValue) {
    throw new TypeError(`${fieldName} must be a non-empty string.`);
  }

  return normalizedValue;
}


function requirePlainObject(value, fieldName) {
  if (
    value === null
    || typeof value !== "object"
    || Array.isArray(value)
  ) {
    throw new TypeError(`${fieldName} must be an object.`);
  }

  return value;
}


function normalizeOptionalObject(value, fieldName) {
  if (value === undefined || value === null) {
    return undefined;
  }

  return requirePlainObject(value, fieldName);
}


function normalizeOptionalPositiveInteger(
  value,
  fieldName,
) {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  const normalizedValue = Number(value);

  if (
    !Number.isInteger(normalizedValue)
    || normalizedValue < 1
  ) {
    throw new TypeError(
      `${fieldName} must be a positive integer.`,
    );
  }

  return normalizedValue;
}


function normalizeOptionalNonNegativeInteger(
  value,
  fieldName,
) {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  const normalizedValue = Number(value);

  if (
    !Number.isInteger(normalizedValue)
    || normalizedValue < 0
  ) {
    throw new TypeError(
      `${fieldName} must be a non-negative integer.`,
    );
  }

  return normalizedValue;
}


function normalizeSupportedValue(
  value,
  supportedValues,
  fieldName,
) {
  const normalizedValue = normalizeOptionalString(value);

  if (!normalizedValue) {
    return undefined;
  }

  if (!supportedValues.includes(normalizedValue)) {
    throw new TypeError(
      `${fieldName} contains an unsupported value.`,
    );
  }

  return normalizedValue;
}


export function buildPurchaseOrdersListParams({
  status = "",
  supplierUuid = "",
  limit,
  offset,
  sortField = "",
  sortDirection = "",
} = {}) {
  const params = {};

  const normalizedStatus = normalizeSupportedValue(
    status,
    PURCHASE_ORDER_STATUSES,
    "status",
  );

  if (normalizedStatus) {
    params.status = normalizedStatus;
  }

  const normalizedSupplierUuid = normalizeOptionalString(
    supplierUuid,
  );

  if (normalizedSupplierUuid) {
    params.supplier_uuid = normalizedSupplierUuid;
  }

  const normalizedLimit = normalizeOptionalPositiveInteger(
    limit,
    "limit",
  );

  if (normalizedLimit !== undefined) {
    params.limit = normalizedLimit;
  }

  const normalizedOffset = normalizeOptionalNonNegativeInteger(
    offset,
    "offset",
  );

  if (normalizedOffset !== undefined) {
    params.offset = normalizedOffset;
  }

  const normalizedSortField = normalizeSupportedValue(
    sortField,
    PURCHASE_ORDER_SORT_FIELDS,
    "sortField",
  );

  if (normalizedSortField) {
    params.sort_field = normalizedSortField;
  }

  const normalizedSortDirection = normalizeSupportedValue(
    sortDirection,
    PURCHASE_ORDER_SORT_DIRECTIONS,
    "sortDirection",
  );

  if (normalizedSortDirection) {
    params.sort_direction = normalizedSortDirection;
  }

  return params;
}


export function buildPurchaseOrderPayload({
  optimizationResult,
  orderReference,
  supplierUuid,
  currency = "EUR",
  metadata,
} = {}) {
  const payload = {
    optimization_result: requirePlainObject(
      optimizationResult,
      "optimizationResult",
    ),
    order_reference: requireNonEmptyString(
      orderReference,
      "orderReference",
    ),
    supplier_uuid: requireNonEmptyString(
      supplierUuid,
      "supplierUuid",
    ),
    currency: (
      normalizeOptionalString(currency)
      || "EUR"
    ),
  };

  const normalizedMetadata = normalizeOptionalObject(
    metadata,
    "metadata",
  );

  if (normalizedMetadata !== undefined) {
    payload.metadata = normalizedMetadata;
  }

  return payload;
}


export function buildPurchaseOrderApprovalPayload({
  metadata,
} = {}) {
  const payload = {};

  const normalizedMetadata = normalizeOptionalObject(
    metadata,
    "metadata",
  );

  if (normalizedMetadata !== undefined) {
    payload.metadata = normalizedMetadata;
  }

  return payload;
}


export function buildPurchaseOrderDispatchPayload({
  connectorKey,
  formatKey,
  destination,
  idempotencyKey,
  attemptNumber = 1,
  metadata,
} = {}) {
  const payload = {
    connector_key: requireNonEmptyString(
      connectorKey,
      "connectorKey",
    ),
    attempt_number: normalizeOptionalPositiveInteger(
      attemptNumber,
      "attemptNumber",
    ) || 1,
  };

  const normalizedFormatKey = normalizeOptionalString(
    formatKey,
  );

  if (normalizedFormatKey) {
    payload.format_key = normalizedFormatKey;
  }

  const normalizedDestination = normalizeOptionalString(
    destination,
  );

  if (normalizedDestination) {
    payload.destination = normalizedDestination;
  }

  const normalizedIdempotencyKey = normalizeOptionalString(
    idempotencyKey,
  );

  if (normalizedIdempotencyKey) {
    payload.idempotency_key = normalizedIdempotencyKey;
  }

  const normalizedMetadata = normalizeOptionalObject(
    metadata,
    "metadata",
  );

  if (normalizedMetadata !== undefined) {
    payload.metadata = normalizedMetadata;
  }

  return payload;
}


export function getPurchaseOrdersErrorMessage(
  error,
  fallback = "Unable to process the purchase order request.",
) {
  const responseData = error?.response?.data;

  if (
    typeof responseData?.message === "string"
    && responseData.message.trim()
  ) {
    return responseData.message.trim();
  }

  if (
    typeof responseData?.error?.message === "string"
    && responseData.error.message.trim()
  ) {
    return responseData.error.message.trim();
  }

  if (
    typeof responseData?.error === "string"
    && responseData.error.trim()
  ) {
    return responseData.error.trim();
  }

  if (
    typeof error?.message === "string"
    && error.message.trim()
  ) {
    return error.message.trim();
  }

  return fallback;
}


export async function listPurchaseOrders({
  companyUuid,
  status = "",
  supplierUuid = "",
  limit,
  offset,
  sortField = "",
  sortDirection = "",
} = {}) {
  const response = await api.get(
    "/supply-chain/purchase-orders",
    {
      headers: buildCompanyScopeHeaders(companyUuid),
      params: buildPurchaseOrdersListParams({
        status,
        supplierUuid,
        limit,
        offset,
        sortField,
        sortDirection,
      }),
    },
  );

  return response.data;
}


export async function getPurchaseOrder(
  orderReference,
  {
    companyUuid,
  } = {},
) {
  const normalizedOrderReference = requireNonEmptyString(
    orderReference,
    "orderReference",
  );

  const response = await api.get(
    `/supply-chain/purchase-orders/${encodeURIComponent(
      normalizedOrderReference,
    )}`,
    {
      headers: buildCompanyScopeHeaders(companyUuid),
    },
  );

  return response.data;
}


export async function createPurchaseOrder(
  purchaseOrder,
  {
    companyUuid,
  } = {},
) {
  const response = await api.post(
    "/supply-chain/purchase-orders",
    buildPurchaseOrderPayload(purchaseOrder),
    {
      headers: buildCompanyScopeHeaders(companyUuid),
    },
  );

  return response.data;
}


export async function approvePurchaseOrder(
  orderReference,
  approval = {},
  {
    companyUuid,
  } = {},
) {
  const normalizedOrderReference = requireNonEmptyString(
    orderReference,
    "orderReference",
  );

  const response = await api.post(
    `/supply-chain/purchase-orders/${encodeURIComponent(
      normalizedOrderReference,
    )}/approve`,
    buildPurchaseOrderApprovalPayload(approval),
    {
      headers: buildCompanyScopeHeaders(companyUuid),
    },
  );

  return response.data;
}


export async function dispatchPurchaseOrder(
  orderReference,
  dispatch,
  {
    companyUuid,
  } = {},
) {
  const normalizedOrderReference = requireNonEmptyString(
    orderReference,
    "orderReference",
  );

  const response = await api.post(
    `/supply-chain/purchase-orders/${encodeURIComponent(
      normalizedOrderReference,
    )}/dispatch`,
    buildPurchaseOrderDispatchPayload(dispatch),
    {
      headers: buildCompanyScopeHeaders(companyUuid),
    },
  );

  return response.data;
}

export async function exportPurchaseOrder(
  orderReference,
  formatKey = "json",
  {
    companyUuid,
  } = {},
) {
  const normalizedOrderReference = requireNonEmptyString(
    orderReference,
    "orderReference",
  );

  const normalizedFormatKey = normalizeSupportedValue(
    formatKey,
    PURCHASE_ORDER_EXPORT_FORMATS,
    "formatKey",
  ) || "json";

  const response = await api.get(
    `/supply-chain/purchase-orders/${encodeURIComponent(
      normalizedOrderReference,
    )}/export`,
    {
      headers: buildCompanyScopeHeaders(companyUuid),
      params: {
        format: normalizedFormatKey,
      },
      responseType: "blob",
    },
  );

  const contentType =
    response.headers?.["content-type"]
    || response.data?.type
    || "application/octet-stream";

  const blob = response.data instanceof Blob
    ? response.data
    : new Blob(
      [response.data],
      {
        type: contentType,
      },
    );

  return {
    blob,
    contentType,
    contentDisposition:
      response.headers?.["content-disposition"]
      || "",
    checksum:
      response.headers?.["x-content-checksum"]
      || "",
  };
}
